import React from 'react';
import {
  Box,
  TextField,
  Button,
  Tabs,
  Tab,
  Autocomplete,
  CircularProgress,
  Alert,
  Collapse,
  FormControlLabel,
  Checkbox,
  Typography,
  Switch,
  Stack,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../../config/api.config';
import { colors } from '../../themes/typography';
import { responsiveSpacing } from '../../themes/responsive';
import { DEFAULT_EXPERIMENT_WEEKS } from '../../constants/experiments';
import BulkActionsTab from '../../pages/ExperimentManagement/components/BulkActions/BulkActionsTab';
import { fetchBulkTargets } from '../../pages/ExperimentManagement/services/bulkActionsApi';
import { fetchAvailableTags } from '../../services/tagsApi';
import TagSelect from '../../pages/ExperimentManagement/components/BulkActions/TagSelect';
import { computeDefaultEndDate, calculateDurationWeeks, formatWeeksDisplay } from '../../utils/experimentDuration';
import { fetchCandidateSuggestion } from '../../pages/ExperimentManagement/services/experimentManagementApi';
import AICandidateChip from './AICandidateChip';
import { MachineQuadrant, alternativeGroupLabel } from './ReplacementAlternatives';
import SlotPicker from './SlotPicker';
import { formatLocalDate } from '../../utils/dateUtils';

/**
 * Create Experiment Form Component
 * Context-aware form for creating price discount experiments
 */

// Mismos cuatro tender (y claves i18n) que AppliedPricingDialog.
const ERP_TENDER_FIELDS = [
  ['cash', 'appliedPriceCash'], ['card', 'appliedPriceCard'],
  ['bank', 'appliedPriceBank'], ['mobile', 'appliedPriceMobile'],
];
const EMPTY_ERP_PRICES = { cash: '', card: '', bank: '', mobile: '' };
// Statuses that put an experiment in motion: none can go on an inactive machine (same rule as the backend)
const LIVE_STATUSES = ['pending', 'accepted', 'running'];

const CreateExperimentForm = ({
  open,
  column,
  currentFilters,
  filterOptions,
  clientId,
  onClose,
  onSuccess,
  onError,
  t,
  // Optional hooks and overrides for advanced flows
  beforeSubmit, // async function to run before POST (e.g., mark alert not broken)
  extraRequestBody = {}, // merged into POST body (e.g., { alertId })
  initialValues, // optional initial values: { dStoreId, dProductId, dateFrom, dateTo }
  lockMachine = false,
  lockProduct = false,
  presetSelection, // optional: { dStoreId, dProductId, machineLabel, productLabel }
  // Alert-driven surfaces pass false: an alert means "this product hasn't sold in N weeks", so
  // offering to RAISE its price there makes no sense.
  allowPriceIncrease = true
}) => {
  // Form state
  const [formData, setFormData] = React.useState({
    experimentType: 'promo_precio_unitario',
    dStoreId: '',
    dProductId: '',
    discount: 15,
    markup: 15,
    dateFrom: null,
    dateTo: null,
    newDProductId: '', // New field for product replacement
    innovation: false, // Innovation checkbox
    tags: [],
    pricingMode: 'discount',
    priceDirection: 'down', // 'down' = descuento, 'up' = subida de precio
    laneScope: 'all', // 'all' = reemplazo completo, 'partial' = ampliar carril
    targetPrice: '',
    isAi: false // Track if AI recommendation was used
  });

  const [activeTab, setActiveTab] = React.useState(0);
  const isBulkMode = activeTab === 2;
  const isPriceIncrease = formData.priceDirection === 'up';
  const isLaneExpansion = formData.experimentType === 'ampliar_carril';

  const handleStartDateChange = React.useCallback((date) => {
    setFormData(prev => ({
      ...prev,
      dateFrom: date,
      dateTo: computeDefaultEndDate(date),
    }));
  }, []);

  const durationDisplay = React.useMemo(() => {
    const rawWeeks = calculateDurationWeeks(formData.dateFrom, formData.dateTo);
    return formatWeeksDisplay(rawWeeks);
  }, [formData.dateFrom, formData.dateTo]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      // Leaving and re-entering the price tab always comes back as a discount.
      setFormData(prev => ({ ...prev, experimentType: 'promo_precio_unitario', priceDirection: 'down' }));
    } else if (newValue === 1) {
      setFormData(prev => ({
        ...prev,
        experimentType: prev.laneScope === 'partial' ? 'ampliar_carril' : 'reemplazo_producto'
      }));
    }
  };

  // The direction is the single input; `experimentType` follows it so handleSubmit keeps reading
  // one source of truth. `markup` is the mirror of `discount` — same units, opposite direction.
  const handlePriceDirectionChange = (event, newDirection) => {
    if (!newDirection) return; // ignore de-selecting the active segment
    setFormData(prev => ({
      ...prev,
      priceDirection: newDirection,
      experimentType: newDirection === 'up' ? 'alza_precio_sugerido' : 'promo_precio_unitario',
      targetPrice: '',
      isAi: false
    }));
  };

  // El alcance es la única entrada; `experimentType` lo sigue, igual que hace la dirección de
  // precio: un subconjunto de carriles es `ampliar_carril`, todos los del donante es
  // `reemplazo_producto`. El split es un concepto de reemplazo completo, así que se apaga al
  // pasar a ampliación.
  const handleLaneScopeChange = (event, newScope) => {
    if (!newScope) return; // ignore de-selecting the active segment
    if (newScope === 'partial') {
      setSplitMode(false);
      setSplitNewDProductId('');
      setSplitSelectionId1('');
      setSplitSelectionId2('');
      setSplitError('');
    }
    setFormData(prev => ({
      ...prev,
      laneScope: newScope,
      experimentType: newScope === 'partial' ? 'ampliar_carril' : 'reemplazo_producto',
      isAi: false
    }));
  };

  // Data loading state
  const [availableData, setAvailableData] = React.useState({
    machines: [],
    products: [],
    allProducts: [], // For replacement product dropdown
    loading: false
  });
  const [bulkTargets, setBulkTargets] = React.useState({ sectors: [], machines: [] });
  const [availableTags, setAvailableTags] = React.useState([]);

  // Double slot split state
  const [splitMode, setSplitMode] = React.useState(false);
  const [splitNewDProductId, setSplitNewDProductId] = React.useState('');
  const [splitSelectionId1, setSplitSelectionId1] = React.useState('');
  const [splitSelectionId2, setSplitSelectionId2] = React.useState('');
  const [splitError, setSplitError] = React.useState('');

  // Form submission state
  const [submitting, setSubmitting] = React.useState(false);
  const [dateError, setDateError] = React.useState('');

  // AI suggestion state
  const [aiSuggestion, setAiSuggestion] = React.useState(null);
  const [aiLoading, setAiLoading] = React.useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = React.useState(null);
  const [otherTypeAvailable, setOtherTypeAvailable] = React.useState(null);
  // Auto-cambio a Subida cuando el par (máquina, producto) tiene una alza pendiente — una
  // sola vez por par, para no pelear con el usuario si vuelve a Bajada a mano.
  const autoSwitchedAlzaRef = React.useRef(null);

  // Slot selection state
  const [selectedSlots, setSelectedSlots] = React.useState([]);
  const [machineSlots, setMachineSlots] = React.useState([]);
  const [slotsLoading, setSlotsLoading] = React.useState(false);

  // Precios del entrante en un reemplazo/ampliación, uno por tender (mismo contrato que el
  // diálogo de precios al aceptar). Se precargan con el precio habitual (pricing-preview) y
  // viajan como `pricingOverride` en el create: el backend los persiste en problem_kpis y el
  // gateway del ERP programa exactamente eso.
  const [erpPrices, setErpPrices] = React.useState(EMPTY_ERP_PRICES);
  const [erpPriceLoading, setErpPriceLoading] = React.useState(false);
  // El gateway del ERP no está configurado para todos los clientes: si pricing-preview no
  // devuelve precios, el cliente no tiene ERP y este bloque no debe mostrarse.
  const [showErpPrices, setShowErpPrices] = React.useState(false);
  // El cliente SÍ tiene ERP pero el producto no tiene precio en ningún sitio (reason
  // 'no_price_history'): el bloque se muestra igual, pero vacío y obligatorio, porque sin un
  // precio tecleado el push al ERP fallaría igual que fallaría con pricingOverride ausente.
  const [erpPricesRequired, setErpPricesRequired] = React.useState(false);

  // Compute if all product slots are occupied by active experiments
  const allSlotsOccupied = React.useMemo(() => {
    if (!formData.dProductId || !machineSlots.length || isBulkMode || formData.experimentType === 'promo_combo_combinado') return false;
    const productSlots = machineSlots.filter(s => s.dProductId === parseInt(formData.dProductId));
    if (productSlots.length === 0) return false;
    const occupiedCount = productSlots.filter(s => !!s.activeExperimentId).length;
    return occupiedCount === productSlots.length;
  }, [formData.dProductId, formData.experimentType, machineSlots, isBulkMode]);

  const occupiedExperimentCount = React.useMemo(() => {
    if (!formData.dProductId || !machineSlots.length) return 0;
    const productSlots = machineSlots.filter(s => s.dProductId === parseInt(formData.dProductId));
    const uniqueExpIds = new Set(productSlots.filter(s => s.activeExperimentId).map(s => s.activeExperimentId));
    return uniqueExpIds.size;
  }, [formData.dProductId, machineSlots]);

  // Warning state for smart validations
  const [warnings, setWarnings] = React.useState([]);
  const [correctedStatus, setCorrectedStatus] = React.useState(column.id);

  // Clear AI suggestions immediately when machine changes (before product is cleared)
  // This fixes the race condition where old suggestions persist during machine change
  React.useEffect(() => {
    setAiSuggestion(null);
    setSelectedCandidateId(null);
    setOtherTypeAvailable(null);
  }, [formData.dStoreId]);

  // Fetch machine slots when machine changes
  React.useEffect(() => {
    if (!formData.dStoreId) {
      setMachineSlots([]);
      setSelectedSlots([]);
      return;
    }
    const fetchSlots = async () => {
      setSlotsLoading(true);
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.get(
          `${API_BASE_URL}/experiments/machine-slots`,
          { params: { clientId, dStoreId: formData.dStoreId }, headers: createAuthHeader(token) }
        );
        setMachineSlots(response.data.slots || []);
      } catch (err) {
        setMachineSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    };
    fetchSlots();
  }, [formData.dStoreId, clientId]);

  // Reset slots when product changes
  React.useEffect(() => {
    setSelectedSlots([]);
  }, [formData.dProductId]);

  // Pre-fill dates from existing slot experiments when product changes
  React.useEffect(() => {
    if (!formData.dProductId || !machineSlots.length) return;
    const productSlots = machineSlots.filter(s =>
      s.dProductId === parseInt(formData.dProductId) && s.activeExperimentId
    );
    if (productSlots.length === 0) return;
    const slot = productSlots[0];
    if (slot.experimentDateFrom && slot.experimentDateTo) {
      setFormData(prev => ({
        ...prev,
        dateFrom: new Date(slot.experimentDateFrom),
        dateTo: new Date(slot.experimentDateTo),
      }));
    }
  }, [formData.dProductId, machineSlots]);

  // Clear replacement product when product is cleared
  React.useEffect(() => {
    if (!formData.dProductId) {
      setFormData(prev => prev.newDProductId ? { ...prev, newDProductId: '' } : prev);
    }
  }, [formData.dProductId]);

  // Fetch the entrant's typical prices when it is chosen (replacement / lane expansion only).
  const isReplacementShaped = formData.experimentType === 'reemplazo_producto' || formData.experimentType === 'ampliar_carril';
  React.useEffect(() => {
    if (!isReplacementShaped || splitMode || isBulkMode || !formData.dStoreId || !formData.dProductId || !formData.newDProductId) {
      setErpPrices(EMPTY_ERP_PRICES);
      setShowErpPrices(false);
      setErpPricesRequired(false);
      return;
    }
    let cancelled = false;
    const fetchTypicalPrice = async () => {
      setErpPriceLoading(true);
      try {
        const token = localStorage.getItem('access_token');
        const response = await axios.post(
          `${API_BASE_URL}/experiments/pricing-preview?clientId=${clientId}`,
          {
            dStoreId: Number(formData.dStoreId),
            dProductId: Number(formData.dProductId),
            newDProductId: Number(formData.newDProductId),
            experimentType: formData.experimentType
          },
          { headers: createAuthHeader(token) }
        );
        if (cancelled) return;
        const pricing = response.data?.pricing;
        const noPriceHistory = response.data?.reason === 'no_price_history';
        setShowErpPrices(Boolean(pricing) || noPriceHistory);
        setErpPricesRequired(noPriceHistory);
        const next = { ...EMPTY_ERP_PRICES };
        ERP_TENDER_FIELDS.forEach(([field]) => {
          const value = Number(pricing?.[field]);
          if (Number.isFinite(value) && value > 0) next[field] = String(value);
        });
        setErpPrices(next);
      } catch {
        if (!cancelled) { setErpPrices(EMPTY_ERP_PRICES); setShowErpPrices(false); setErpPricesRequired(false); }
      } finally {
        if (!cancelled) setErpPriceLoading(false);
      }
    };
    fetchTypicalPrice();
    return () => { cancelled = true; };
  }, [isReplacementShaped, splitMode, isBulkMode, formData.dStoreId, formData.dProductId, formData.newDProductId, formData.experimentType, clientId]);

  const showPriceCommaWarning = React.useMemo(() => {
    if (formData.pricingMode !== 'price') return false;
    if (typeof formData.targetPrice !== 'string') return false;
    return formData.targetPrice.includes(',');
  }, [formData.pricingMode, formData.targetPrice]);

  // Date validation function for smart status correction and warnings
  const validateExperimentDates = React.useCallback((dateFrom, dateTo, originalColumnId, filterDateRange) => {
    if (!dateFrom || !dateTo) {
      return { warnings: [], correctedStatus: originalColumnId };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0); // Normalize to start of day for accurate comparison
    
    const experimentStart = new Date(dateFrom);
    experimentStart.setHours(0, 0, 0, 0);
    
    const experimentEnd = new Date(dateTo);
    experimentEnd.setHours(23, 59, 59, 999); // End of day for accurate comparison
    
    const warnings = [];
    let correctedStatus = originalColumnId;
    
    // Status correction logic based on dates
    if (experimentEnd < today) {
      // Past experiment - should be finished
      correctedStatus = 'finished';
      if (originalColumnId !== 'finished') {
        warnings.push({
          type: 'status',
          severity: 'warning',
          message: t('experimentMovedToFinished') || 'This experiment will be moved to the Finished column due to past dates.'
        });
      }
    } else if (experimentStart <= today && experimentEnd >= today) {
      // Current experiment - should be running
      correctedStatus = 'running';
      if (originalColumnId !== 'running') {
        warnings.push({
          type: 'status',
          severity: 'warning',
          message: t('experimentMovedToRunning') || 'This experiment will be moved to the Running column due to current dates.'
        });
      }
    } else if (experimentStart > today) {
      // Future experiment - keep original status unless it's running/finished
      if (originalColumnId === 'running') {
        correctedStatus = 'pending';
        warnings.push({
          type: 'status',
          severity: 'warning',
          message: t('experimentMovedToPending') || 'This experiment will be moved to the Pending column due to future dates.'
        });
      } else if (originalColumnId === 'finished') {
        correctedStatus = 'pending';
        warnings.push({
          type: 'status',
          severity: 'warning',
          message: t('experimentMovedToPending') || 'This experiment will be moved to the Pending column due to future dates.'
        });
      }
    }
    
    // Filter visibility check - check if experiment overlaps with current filter date range
    if (filterDateRange && filterDateRange.start && filterDateRange.end) {
      const filterStart = new Date(filterDateRange.start);
      filterStart.setHours(0, 0, 0, 0);
      
      const filterEnd = new Date(filterDateRange.end);
      filterEnd.setHours(23, 59, 59, 999);
      
      // Check for any overlap: experiment starts before filter ends AND experiment ends after filter starts
      const hasOverlap = experimentStart <= filterEnd && experimentEnd >= filterStart;
      
      if (!hasOverlap) {
        warnings.push({
          type: 'visibility',
          severity: 'info',
          message: t('experimentNotVisible') || "This experiment won't be visible with current date filters. Update your date range to see it after creation."
        });
      }
    }
    
    return { warnings, correctedStatus };
  }, [t]);

  // Initialize default dates based on column rules or initialValues
  React.useEffect(() => {
    if (!open) return;
    
    let defaultStartDate = initialValues?.dateFrom;
    let defaultEndDate = initialValues?.dateTo;
    
    if (!defaultStartDate || !defaultEndDate) {
      if (column.id === 'running') {
      // Running experiments: start date before today
        defaultStartDate = new Date();
        defaultStartDate.setDate(defaultStartDate.getDate() - 1); // Yesterday
        
        // End date is start + DEFAULT_EXPERIMENT_WEEKS
        defaultEndDate = new Date(defaultStartDate);
        defaultEndDate.setDate(defaultEndDate.getDate() + (DEFAULT_EXPERIMENT_WEEKS * 7));
      } else if (column.id === 'finished') {
      // Finished experiments: both dates before today
        // End = yesterday
        defaultEndDate = new Date();
        defaultEndDate.setDate(defaultEndDate.getDate() - 1);
        // Start = end - DEFAULT_EXPERIMENT_WEEKS
        defaultStartDate = new Date(defaultEndDate);
        defaultStartDate.setDate(defaultStartDate.getDate() - (DEFAULT_EXPERIMENT_WEEKS * 7));
      } else {
      // Default for pending/accepted: 1st of next month + 8 weeks
        defaultStartDate = new Date();
        defaultStartDate.setMonth(defaultStartDate.getMonth() + 1, 1);
        defaultStartDate.setHours(0, 0, 0, 0);

        // End date is start + DEFAULT_EXPERIMENT_WEEKS
        defaultEndDate = new Date(defaultStartDate);
        defaultEndDate.setDate(defaultEndDate.getDate() + (DEFAULT_EXPERIMENT_WEEKS * 7));
      }
    }
    
    setFormData(prev => ({
      ...prev,
      experimentType: initialValues?.experimentType ?? prev.experimentType,
      // A price increase is the price tab with the direction flipped, not a tab of its own.
      priceDirection: initialValues?.experimentType === 'alza_precio_sugerido' ? 'up' : prev.priceDirection,
      // A lane expansion is the replacement tab with the scope narrowed, same pattern.
      laneScope: initialValues?.experimentType === 'ampliar_carril' ? 'partial'
        : initialValues?.experimentType === 'reemplazo_producto' ? 'all'
        : prev.laneScope,
      dateFrom: defaultStartDate,
      dateTo: defaultEndDate,
      dStoreId: initialValues?.dStoreId ?? prev.dStoreId,
      dProductId: initialValues?.dProductId ?? prev.dProductId
    }));

    // Sync activeTab with experimentType from initialValues
    if (
      initialValues?.experimentType === 'reemplazo_producto' ||
      initialValues?.experimentType === 'ampliar_carril'
    ) {
      setActiveTab(1); // Tab 1 = Replacement (full or lane expansion)
    } else if (
      initialValues?.experimentType === 'promo_precio_unitario' ||
      initialValues?.experimentType === 'alza_precio_sugerido'
    ) {
      setActiveTab(0); // Tab 0 = Price (down or up)
    }
  }, [open, column.id, initialValues]);

  // Ensure initial machine/product appear in dropdown options for preselection
  React.useEffect(() => {
    if (!open) return;
    setAvailableData(prev => {
      const next = { ...prev };
      // Inject initial machine option if not present
      if (initialValues?.dStoreId) {
        const exists = (prev.machines || []).some(m => m.id === initialValues.dStoreId);
        if (!exists) {
          const displayName = initialValues.machineAlias || initialValues.machineDisplayName || `Machine ${initialValues.dStoreId}`;
          next.machines = [{ id: initialValues.dStoreId, displayName }, ...(prev.machines || [])];
        }
      }
      // Inject initial product option if not present
      if (initialValues?.dProductId) {
        const existsP = (prev.products || []).some(p => p.id === initialValues.dProductId);
        if (!existsP) {
          const name = initialValues.productName || '';
          next.products = [{ id: initialValues.dProductId, name }, ...(prev.products || [])];
        }
      }
      return next;
    });
  }, [open, initialValues]);

  // Compute Autocomplete values based on IDs and options
  const machineValue = React.useMemo(() => {
    const id = formData.dStoreId;
    const fromList = (availableData.machines || []).find(m => String(m.id) === String(id));
    if (fromList) return fromList;
    if (id) {
      return {
        id,
        displayName: presetSelection?.machineLabel || initialValues?.machineAlias || initialValues?.machineDisplayName || `Machine ${id}`
      };
    }
    return null;
  }, [formData.dStoreId, availableData.machines, presetSelection, initialValues]);

  // An inactive machine (no live sales or no recent planogram) cannot take an experiment that goes
  // live; recording a finished or rejected one stays possible, same rule as the backend.
  const machineInactive = machineValue?.active === false && LIVE_STATUSES.includes(correctedStatus);

  const productValue = React.useMemo(() => {
    const id = formData.dProductId;
    const fromList = (availableData.products || []).find(p => String(p.id) === String(id));
    if (fromList) return fromList;
    if (id) {
      return {
        id,
        name: presetSelection?.productLabel || initialValues?.productName || ''
      };
    }
    return null;
  }, [formData.dProductId, availableData.products, presetSelection, initialValues]);

  // Get context-aware machines based on current filters
  const getAvailableMachines = React.useCallback(() => {
    if (!filterOptions.groups || filterOptions.groups.length === 0) {
      return [];
    }

    const { group, cluster, location } = currentFilters;
    
    // If no group filter, show all machines from all groups
    if (!group) {
      const allMachines = [];
      filterOptions.groups.forEach(g => {
        g.clusters?.forEach(c => {
          c.locations?.forEach(l => {
            l.machines?.forEach(m => {
            const alias = m.alias || `Machine ${m.id}`;
            allMachines.push({
              ...m,
              displayName: alias,
              groupName: g.name,
              locationName: l.name
            });
            });
          });
        });
      });
      return allMachines;
    }

    const selectedGroup = filterOptions.groups.find(g => g.id === group);
    if (!selectedGroup) return [];

    // If no location filter, show all machines in the group
    if (!location) {
      const groupMachines = [];
      selectedGroup.clusters?.forEach(c => {
        c.locations?.forEach(l => {
          l.machines?.forEach(m => {
            const alias = m.alias || `Machine ${m.id}`;
            groupMachines.push({
              ...m,
              displayName: alias,
              groupName: selectedGroup.name,
              locationName: l.name
            });
          });
        });
      });
      return groupMachines;
    }

    // Show machines only from selected location
    let selectedLocation = null;
    selectedGroup.clusters?.forEach(c => {
      const loc = c.locations?.find(l => l.id === location);
      if (loc) {
        selectedLocation = loc;
      }
    });
    
    if (!selectedLocation) return [];

    return selectedLocation.machines?.map(m => ({
      ...m,
      displayName: m.alias || `Machine ${m.id}`,
      groupName: selectedGroup.name,
      locationName: selectedLocation.name
    })) || [];
  }, [currentFilters, filterOptions]);

  // Function to load all products (fallback and initial load)
  const loadAllProducts = React.useCallback(async () => {
    setAvailableData(prev => ({ ...prev, loading: true }));
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API_BASE_URL}/all-products?clientId=${clientId}`,
        { headers: createAuthHeader(token) }
      );
      
      setAvailableData(prev => ({
        ...prev,
        products: response.data.products || [],
        allProducts: response.data.products || [], // Also store as allProducts for replacement dropdown
        loading: false
      }));
    } catch (error) {
      onError?.(t('failedToLoadFormData') || 'Failed to load form data');
      setAvailableData(prev => ({ ...prev, loading: false }));
    }
  }, [clientId, onError, t]);

  // Function to load machine-specific products
  const loadMachineProducts = React.useCallback(async (machineId) => {
    setAvailableData(prev => ({ ...prev, loading: true }));
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API_BASE_URL}/machine-products?clientId=${clientId}&machineId=${machineId}`,
        { headers: createAuthHeader(token) }
      );
      
      setAvailableData(prev => ({
        ...prev,
        products: response.data.products || [],
        loading: false
      }));
      
      // Clear selected product when machine changes to avoid invalid combinations
      // Do not clear if product is locked (coming from alerts) or already preset
      setFormData(prev => {
        if (lockProduct) return prev;
        return { ...prev, dProductId: '' };
      });
    } catch (error) {
      // Fallback to all products
      loadAllProducts();
    }
  }, [clientId, loadAllProducts]);

  // Load form data when form opens
  React.useEffect(() => {
    if (open) {
      const loadFormData = async () => {
        try {
          const token = localStorage.getItem('access_token');

          // Fetch machines with problem/alert counts from /machine-options endpoint
          // This gives us bubbles for the machine dropdown
          let machinesWithCounts = [];
          try {
            const machineOptionsResponse = await axios.get(
              `${API_BASE_URL}/machine-options?clientId=${clientId}`,
              { headers: createAuthHeader(token) }
            );
            machinesWithCounts = (machineOptionsResponse.data.machines || []).map(m => ({
              ...m,
              displayName: m.alias || m.storeId || `Machine ${m.id}`,
            }));
          } catch (machineOptionsError) {
            console.error('Failed to load machine options with counts:', machineOptionsError);
            // Fallback to context-aware machines without counts
            machinesWithCounts = getAvailableMachines();
          }

          setAvailableData(prev => ({
            ...prev,
            machines: machinesWithCounts,
            loading: false
          }));

          // Load all products initially (will be replaced by machine-specific products when machine is selected)
          loadAllProducts();

          try {
            const targets = await fetchBulkTargets(clientId);
            setBulkTargets({
              sectors: targets?.sectors || [],
              machines: targets?.machines || []
            });
          } catch (targetError) {
            console.error('Failed to load bulk targets:', targetError);
            setBulkTargets({ sectors: [], machines: [] });
          }

          try {
            const token = localStorage.getItem('access_token');
            const tags = await fetchAvailableTags(clientId, token);
            setAvailableTags(tags || []);
          } catch (tagError) {
            console.error('Failed to load available tags:', tagError);
            setAvailableTags([]);
          }
        } catch (error) {
          onError?.(t('failedToLoadFormData') || 'Failed to load form data');
          setAvailableData(prev => ({ ...prev, loading: false }));
          setBulkTargets({ sectors: [], machines: [] });
          setAvailableTags([]);
        }
      };

      loadFormData();
    }
  }, [open, getAvailableMachines, clientId, onError, t, loadAllProducts]);

  const handleTagsCommitted = React.useCallback((tags = []) => {
    if (!Array.isArray(tags) || tags.length === 0) return;
    setAvailableTags(prev => {
      const existing = new Set(prev.map(tag => (tag.name || '').toLowerCase()));
      const additions = tags.filter(tag => tag?.name && !existing.has(tag.name.toLowerCase()));
      if (!additions.length) return prev;
      return [...prev, ...additions];
    });
  }, []);

  // Load machine-specific products when machine is selected
  React.useEffect(() => {
    if (formData.dStoreId && open) {
      loadMachineProducts(formData.dStoreId);
    } else if (open && !formData.dStoreId) {
      loadAllProducts(); // Show all products when no machine selected
      // Clear product selections when machine is cleared
      if (!lockProduct) {
        setFormData(prev => ({ ...prev, dProductId: '', newDProductId: '' }));
      }
    }
  }, [formData.dStoreId, open, loadMachineProducts, loadAllProducts, lockProduct]);

  React.useEffect(() => {
    if (isBulkMode) {
      setWarnings([]);
      setDateError('');
      setCorrectedStatus(column.id);
      return;
    }

    // Hard validation: order and minimum length
    if (formData.dateFrom && formData.dateTo) {
      const start = new Date(formData.dateFrom);
      const end = new Date(formData.dateTo);
      if (start > end) {
        setDateError(t('dateRangeInvalidOrder') || 'Start date must be before end date.');
      } else {
        const msPerDay = 1000 * 60 * 60 * 24;
        const days = Math.floor((end.setHours(0,0,0,0) - start.setHours(0,0,0,0)) / msPerDay) + 1;
        if (days < 14) {
          setDateError(t('dateRangeTooShort') || 'Date range must be at least 14 days.');
        } else {
          setDateError('');
        }
      }
    } else {
      setDateError('');
    }

    const { warnings: newWarnings, correctedStatus: newStatus } = validateExperimentDates(
      formData.dateFrom,
      formData.dateTo,
      column.id,
      currentFilters?.dateRange
    );
    
    setWarnings(newWarnings);
    setCorrectedStatus(newStatus);
  }, [formData.dateFrom, formData.dateTo, column.id, currentFilters?.dateRange, validateExperimentDates, isBulkMode]);

  // Fetch AI suggestions when store + product are selected. Para las ALZAS la sugerencia es
  // el experimento pendiente del orquestador para este par (el que cuenta el badge rojo),
  // no una heurística — el endpoint ya lo entiende.
  React.useEffect(() => {
    if (!formData.dStoreId || !formData.dProductId || isBulkMode) {
      setAiSuggestion(null);
      setSelectedCandidateId(null);
      setOtherTypeAvailable(null);
      return;
    }

    const experimentType = activeTab === 0
      ? (isPriceIncrease ? 'alza_precio_sugerido' : 'promo_precio_unitario')
      : 'reemplazo_producto';

    const fetchSuggestion = async () => {
      setAiLoading(true);
      try {
        const result = await fetchCandidateSuggestion(
          clientId,
          formData.dStoreId,
          formData.dProductId,
          experimentType
        );
        // Alza pendiente: cambiar el formulario a Subida automáticamente (una sola vez por
        // par; si el usuario vuelve a Bajada a mano, se queda el hint clicable de siempre).
        if (result?.availableInOtherType === 'alza_precio_sugerido') {
          const pairKey = `${formData.dStoreId}-${formData.dProductId}`;
          if (autoSwitchedAlzaRef.current !== pairKey) {
            autoSwitchedAlzaRef.current = pairKey;
            setAiSuggestion(null);
            setOtherTypeAvailable(null);
            setFormData(prev => ({
              ...prev,
              priceDirection: 'up',
              experimentType: 'alza_precio_sugerido',
              targetPrice: ''
            }));
            return; // el efecto se relanza ya en modo Subida y trae la sugerencia
          }
        }
        // Check if result indicates suggestion available in other type
        if (result?.availableInOtherType) {
          setOtherTypeAvailable({
            type: result.availableInOtherType,
            hint: result.otherTypeHint
          });
          setAiSuggestion(null);
        } else {
          setOtherTypeAvailable(null);
          setAiSuggestion(result);
          // El % de la alza pendiente se rellena solo (editable después).
          if (result?.suggestedMarkup) {
            setFormData(prev => ({ ...prev, markup: result.suggestedMarkup, isAi: true }));
          }
        }
        setSelectedCandidateId(null);
      } catch {
        setAiSuggestion(null);
        setOtherTypeAvailable(null);
      } finally {
        setAiLoading(false);
      }
    };

    fetchSuggestion();
  }, [formData.dStoreId, formData.dProductId, activeTab, clientId, isBulkMode, isPriceIncrease]);

  // Handle form submission
  const handleSubmit = async () => {
    // Basic validation for all experiment types
    if (!formData.dStoreId || !formData.dProductId || !formData.dateFrom || !formData.dateTo) {
      onError?.(t('pleaseCompleteAllFields') || 'Please complete all required fields');
      return;
    }

    if (dateError) {
      onError?.(dateError);
      return;
    }

    // Slot validation: block if all slots occupied
    if (allSlotsOccupied) {
      onError?.(t('allSlotsOccupied', { count: occupiedExperimentCount }) || 'All slots are occupied');
      return;
    }

    // Type-specific validation
    if (formData.experimentType === 'promo_precio_unitario') {
      const usePriceMode = formData.pricingMode === 'price';

      if (usePriceMode) {
        const parsedPrice = parseFloat(formData.targetPrice);
        if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
          onError?.(t('pleaseEnterFinalPrice') || 'Please enter a valid final price');
          return;
        }
      } else {
        if (!formData.discount || formData.discount < 1) {
          onError?.(t('pleaseEnterDiscount') || 'Please enter a discount percentage');
          return;
        }
      }
    }

    if (formData.experimentType === 'alza_precio_sugerido') {
      const usePriceMode = formData.pricingMode === 'price';

      if (usePriceMode) {
        // "Above the current price" can only be checked server-side — the form does not know it.
        const parsedPrice = parseFloat(formData.targetPrice);
        if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
          onError?.(t('pleaseEnterFinalPrice') || 'Please enter a valid final price');
          return;
        }
      } else {
        // Sin tope superior: una subida puede superar el +100% (café casi gratis → tarifa normal).
        if (!formData.markup || formData.markup < 1) {
          onError?.(t('pleaseEnterMarkup') || 'Please enter a price increase of at least 1%');
          return;
        }
      }
    }

    if ((formData.experimentType === 'reemplazo_producto' || isLaneExpansion) && !formData.newDProductId) {
      onError?.(t('pleaseSelectReplacementProduct') || 'Please select a replacement product');
      return;
    }

    // Los 4 tender van juntos o no van: el gateway exige el override completo.
    const erpPriceValues = ERP_TENDER_FIELDS.map(([field]) => parseFloat(erpPrices[field]));
    const anyErpPriceSet = ERP_TENDER_FIELDS.some(([field]) => String(erpPrices[field]).trim() !== '');
    const allErpPricesValid = erpPriceValues.every(v => Number.isFinite(v) && v > 0);
    if ((formData.experimentType === 'reemplazo_producto' || isLaneExpansion) && !splitMode && showErpPrices && erpPricesRequired && !allErpPricesValid) {
      onError?.(t('erpEntrantPriceRequired') || 'Este producto no tiene precio en el ERP: introduce los 4 precios para continuar');
      return;
    }
    if ((formData.experimentType === 'reemplazo_producto' || isLaneExpansion) && !splitMode && showErpPrices && !erpPricesRequired && anyErpPriceSet && !allErpPricesValid) {
      onError?.(t('erpEntrantPricesIncomplete') || 'Completa los 4 precios (efectivo, tarjeta, banco y móvil) o déjalos todos vacíos');
      return;
    }

    // D6: la ampliación se define por QUÉ carriles cambian de manos — sin al menos uno no hay
    // experimento. Sin tope superior: el SlotPicker ya multi-selecciona.
    if (isLaneExpansion && selectedSlots.length < 1) {
      onError?.(t('ampliarCarrilRequiresLane') || 'Selecciona al menos un carril para la ampliación');
      return;
    }

    if (splitMode && formData.experimentType === 'reemplazo_producto' && !splitNewDProductId) {
      onError?.(t('pleaseSelectSecondReplacementProduct') || 'Please select a second replacement product');
      return;
    }

    if (splitMode && formData.experimentType === 'reemplazo_producto') {
      if (!splitSelectionId1.trim() || !splitSelectionId2.trim()) {
        setSplitError(t('pleaseEnterBothSelectionCodes') || 'Please enter selection codes for both positions');
        return;
      }
    }
    setSplitError('');

    setSubmitting(true);
    
    try {
      const token = localStorage.getItem('access_token');

      const normalizedTags = Array.isArray(formData.tags)
        ? formData.tags.filter(tag => tag?.name)
        : [];

      // Build request body based on experiment type
      const requestBody = {
        dStoreId: parseInt(formData.dStoreId),
        dProductId: parseInt(formData.dProductId),
        dateFrom: formatLocalDate(formData.dateFrom),
        dateTo: formatLocalDate(formData.dateTo),
        experimentType: formData.experimentType,
        status: correctedStatus, // Use smart-corrected status instead of column.id
        tags: normalizedTags,
        isAi: formData.isAi
      };

      // Add type-specific fields
      if (formData.experimentType === 'promo_precio_unitario') {
        const usePriceMode = formData.pricingMode === 'price';
        requestBody.pricingMode = usePriceMode ? 'price' : 'discount';

        if (usePriceMode) {
          requestBody.targetPrice = parseFloat(formData.targetPrice);
        } else {
          requestBody.discount = formData.discount;
        }
      } else if (formData.experimentType === 'alza_precio_sugerido') {
        // Never send `discount` for a rise: the row's signature is discount NULL + markup set.
        const usePriceMode = formData.pricingMode === 'price';
        requestBody.pricingMode = usePriceMode ? 'price' : 'discount';

        if (usePriceMode) {
          requestBody.targetPrice = parseFloat(formData.targetPrice);
        } else {
          requestBody.markup = formData.markup;
        }
      } else if (formData.experimentType === 'reemplazo_producto' || formData.experimentType === 'ampliar_carril') {
        requestBody.newDProductId = parseInt(formData.newDProductId);
        // Los precios del entrante viajan como override de los 4 tender: el backend los guarda
        // en problem_kpis (y p1 = efectivo como referencia) y el gateway del ERP programa
        // exactamente eso, al crear aceptado o al aceptar después.
        if (!splitMode && showErpPrices && anyErpPriceSet && allErpPricesValid) {
          const [cash, card, bank, mobile] = erpPriceValues;
          requestBody.pricingOverride = { cash, card, bank, mobile };
          requestBody.targetPrice = cash;
        }
      }
      
      // Add innovation flag
      requestBody.innovation = formData.innovation;

      // Add slot selection
      if (splitMode && splitSelectionId1) {
        // In split mode, use user-provided selection code for position 1
        requestBody.selectionIds = [splitSelectionId1.trim()];
      } else if (selectedSlots.length > 0) {
        requestBody.selectionIds = selectedSlots;
      }

      // Run optional pre-submit hook (e.g., mark alert as not broken)
      if (typeof beforeSubmit === 'function') {
        await beforeSubmit();
      }

      const response = await axios.post(
        `${API_BASE_URL}/experiments/create?clientId=${clientId}`,
        { ...requestBody, ...(extraRequestBody || {}) },
        { headers: createAuthHeader(token) }
      );

      // If split mode, create second experiment with its own selection code
      if (splitMode && formData.experimentType === 'reemplazo_producto' && splitNewDProductId) {
        const splitBody = {
          ...requestBody,
          newDProductId: parseInt(splitNewDProductId),
          splitPair: true
        };
        if (splitSelectionId2.trim()) {
          splitBody.selectionIds = [splitSelectionId2.trim()];
        } else {
          delete splitBody.selectionIds;
        }
        await axios.post(
          `${API_BASE_URL}/experiments/create?clientId=${clientId}`,
          { ...splitBody, ...(extraRequestBody || {}) },
          { headers: createAuthHeader(token) }
        );
      }

      if (normalizedTags.length) {
        handleTagsCommitted(normalizedTags);
      }

      onSuccess?.(response.data);
      onClose();
    } catch (error) {
      const errorKey = error.response?.data?.message;
      // El backend manda messageParams (p.ej. {days: 3} para experimentStartsTooSoon); sin
      // pasarlos a t() el toast enseñaba el placeholder crudo "{{days}}".
      const messageParams = error.response?.data?.messageParams;
      const errorMessage = errorKey
        ? t(errorKey, { defaultValue: errorKey, ...(messageParams || {}) })
        : t('failedToCreateExperiment') || 'Failed to create experiment';
      onError?.(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    // Reset form
    setFormData({
      experimentType: 'promo_precio_unitario',
      dStoreId: '',
      dProductId: '',
      discount: 15,
      markup: 15,
      dateFrom: null,
      dateTo: null,
      newDProductId: '',
      innovation: false,
      tags: [],
      pricingMode: 'discount',
      priceDirection: 'down',
      laneScope: 'all',
      targetPrice: '',
      isAi: false
    });
    setErpPrices(EMPTY_ERP_PRICES);
    setActiveTab(0);
    onClose();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          p: responsiveSpacing.sm,
          width: { xs: 'calc(100vw - 24px)', sm: '380px' },
          maxWidth: '100%'
        }}>

        {/* Experiment Type Tabs */}
        <Box sx={{ mb: responsiveSpacing.sm }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '12px',
                minHeight: '36px'
              }
            }}
          >
            <Tab value={0} label={allowPriceIncrease ? (t('priceTab') || 'Precio') : (t('priceDiscount') || 'Descuento de Precio')} />
            <Tab value={1} label={t('productReplacement') || 'Reemplazo Producto'} />
            <Tab value={2} label={t('bulkActions') || 'Acciones Masivas'} />
          </Tabs>
        </Box>

        {isBulkMode ? (
          <BulkActionsTab
            clientId={clientId}
            availableTargets={bulkTargets}
            availableProducts={availableData.allProducts}
            defaultDates={{
              from: formData.dateFrom,
              to: formData.dateTo,
            }}
            availableTags={availableTags}
            onTagsCommitted={handleTagsCommitted}
            creationStatus={column.id}
            onSuccess={(message) => {
              onSuccess?.(message);
              onClose();
            }}
            onError={onError}
            onCancel={handleCancel}
          />
        ) : (
          <>
            <Collapse in={warnings.length > 0}>
              <Alert
                severity={warnings.find(w => w.type === 'status') ? 'warning' : 'info'}
                sx={{
                  mb: responsiveSpacing.sm,
                  fontSize: '11px',
                  '& .MuiAlert-message': {
                    fontSize: '11px',
                    lineHeight: 1.3
                  }
                }}
              >
                {warnings.map((warning, index) => (
                  <span key={index}>
                    {warning.message}
                    {index < warnings.length - 1 && ' '}
                  </span>
                ))}
              </Alert>
            </Collapse>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: responsiveSpacing.sm }}>
          {/* Common Fields - Always shown */}
          {/* Machine field: lockable when coming from alerts */}
          <Autocomplete
            options={availableData.machines}
            getOptionLabel={(option) => option.displayName || option.alias || ''}
            isOptionEqualToValue={(opt, val) => (opt?.id || null) === (val?.id || null)}
            value={machineValue}
            onChange={(_, newValue) => {
              setFormData(prev => ({ ...prev, dStoreId: newValue?.id || '' }));
            }}
            disabled={availableData.loading || lockMachine}
            getOptionDisabled={(option) => option?.active === false && LIVE_STATUSES.includes(correctedStatus)}
            renderOption={(props, option) => {
              const problemCount = Number(option?.problemCount ?? 0);
              const alertCount = Number(option?.alertCount ?? 0);
              return (
                <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {option.displayName || option.alias || ''}
                    {option?.active === false && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t('machineStatusInactive')} · {t(`machineInactiveReason_${option.inactiveReason}`)}
                      </Typography>
                    )}
                  </span>
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0 }}>
                    {problemCount > 0 && (
                      <Box sx={{
                        minWidth: 24, px: 0.75, height: 20, borderRadius: '999px',
                        backgroundColor: '#fee2e2', color: '#b91c1c',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.75rem', fontWeight: 600
                      }}>
                        {problemCount}
                      </Box>
                    )}
                    {alertCount > 0 && (
                      <Box sx={{
                        minWidth: 24, px: 0.75, height: 20, borderRadius: '999px',
                        backgroundColor: '#f3e8ff', color: '#7c3aed',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.75rem', fontWeight: 600
                      }}>
                        {alertCount}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => {
              const selectedOpt = availableData.machines.find(m => String(m.id) === String(formData.dStoreId));
              const problemCount = Number(selectedOpt?.problemCount ?? 0);
              const alertCount = Number(selectedOpt?.alertCount ?? 0);
              return (
                <TextField
                  {...params}
                  label={t('machine') || 'Machine'}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        <Box sx={{ display: 'flex', gap: 0.5, mr: 0.5 }}>
                          {problemCount > 0 && (
                            <Box sx={{
                              minWidth: 24, px: 0.75, height: 20, borderRadius: '999px',
                              backgroundColor: '#fee2e2', color: '#b91c1c',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.75rem', fontWeight: 600
                            }}>
                              {problemCount}
                            </Box>
                          )}
                          {alertCount > 0 && (
                            <Box sx={{
                              minWidth: 24, px: 0.75, height: 20, borderRadius: '999px',
                              backgroundColor: '#f3e8ff', color: '#7c3aed',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.75rem', fontWeight: 600
                            }}>
                              {alertCount}
                            </Box>
                          )}
                        </Box>
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              );
            }}
          />

          {machineInactive && (
            <Alert severity="warning" sx={{ py: 0.5, fontSize: '12px' }}>
              {t('machineInactiveCannotCreate', { reason: t(`machineInactiveReason_${machineValue.inactiveReason}`) })}
            </Alert>
          )}

          {/* Product from machine: lockable */}
          <Autocomplete
            options={availableData.products}
            getOptionLabel={(option) => option.name || ''}
            isOptionEqualToValue={(opt, val) => (opt?.id || null) === (val?.id || null)}
            value={productValue}
            onChange={(_, newValue) => {
              setFormData(prev => ({ ...prev, dProductId: newValue?.id || '' }));
            }}
            disabled={availableData.loading || lockProduct}
            renderOption={(props, option) => {
              const problemCount = Number(option?.problemCount ?? 0);
              const alertCount = Number(option?.alertCount ?? 0);
              return (
                <Box component="li" {...props} sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                    {option.name || ''}
                  </span>
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 1, flexShrink: 0 }}>
                    {problemCount > 0 && (
                      <Box sx={{
                        minWidth: 24, px: 0.75, height: 20, borderRadius: '999px',
                        backgroundColor: '#fee2e2', color: '#b91c1c',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.75rem', fontWeight: 600
                      }}>
                        {problemCount}
                      </Box>
                    )}
                    {alertCount > 0 && (
                      <Box sx={{
                        minWidth: 24, px: 0.75, height: 20, borderRadius: '999px',
                        backgroundColor: '#f3e8ff', color: '#7c3aed',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.75rem', fontWeight: 600
                      }}>
                        {alertCount}
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => {
              const selectedOpt = availableData.products.find(p => String(p.id) === String(formData.dProductId));
              const problemCount = Number(selectedOpt?.problemCount ?? 0);
              const alertCount = Number(selectedOpt?.alertCount ?? 0);
              return (
                <TextField
                  {...params}
                  label={activeTab === 0
                    ? (t('product') || 'Product')
                    : isLaneExpansion
                      ? (t('donorProductLabel') || 'Producto que cede el carril')
                      : (t('productFromMachine') || 'Product from Machine')}
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        <Box sx={{ display: 'flex', gap: 0.5, mr: 0.5 }}>
                          {problemCount > 0 && (
                            <Box sx={{
                              minWidth: 24, px: 0.75, height: 20, borderRadius: '999px',
                              backgroundColor: '#fee2e2', color: '#b91c1c',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.75rem', fontWeight: 600
                            }}>
                              {problemCount}
                            </Box>
                          )}
                          {alertCount > 0 && (
                            <Box sx={{
                              minWidth: 24, px: 0.75, height: 20, borderRadius: '999px',
                              backgroundColor: '#f3e8ff', color: '#7c3aed',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.75rem', fontWeight: 600
                            }}>
                              {alertCount}
                            </Box>
                          )}
                        </Box>
                        {availableData.loading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              );
            }}
          />

          {/* Slot Picker - shown when machine and product are selected, not combo, not bulk */}
          {formData.dStoreId && formData.dProductId && formData.experimentType !== 'promo_combo_combinado' && !isBulkMode && machineSlots.length > 0 && (
            <SlotPicker
              slots={machineSlots}
              productId={parseInt(formData.dProductId)}
              selectedSlots={selectedSlots}
              onChange={setSelectedSlots}
              loading={slotsLoading}
              t={t}
            />
          )}

          {/* All slots occupied warning */}
          {allSlotsOccupied && (
            <Box sx={{
              mt: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: '8px',
              backgroundColor: '#FEF2F2',
              border: '1px solid #FECACA',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
              <Typography sx={{
                fontSize: '12px',
                color: '#991B1B',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
              }}>
                {t('allSlotsOccupied', {
                  count: occupiedExperimentCount,
                  defaultValue: `Todos los carriles están ocupados en ${occupiedExperimentCount} experimento(s) activo(s)`
                })}
              </Typography>
            </Box>
          )}

          {/* Candidates Loading/No Candidates Message - shown below product dropdown */}
          {formData.dStoreId && formData.dProductId && !isBulkMode && (
            <>
              {/* Loading State */}
              {aiLoading && (
                <Box sx={{
                  p: 2,
                  borderRadius: 1,
                  background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
                  border: '1px solid #C4B5FD',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5
                }}>
                  <CircularProgress size={16} sx={{ color: '#7C3AED' }} />
                  <Typography sx={{ fontSize: '11px', color: '#7C3AED', fontWeight: 500 }}>
                    {activeTab === 0
                      ? t('searchingDiscountSuggestions', { defaultValue: 'Searching for discount suggestions...' })
                      : t('searchingReplacementCandidates', { defaultValue: 'Searching for replacement candidates...' })
                    }
                  </Typography>
                </Box>
              )}

              {/* No Candidates Found */}
              {!aiLoading && !otherTypeAvailable && (
                <>
                  {activeTab === 0 && !aiSuggestion?.suggestedDiscount && !aiSuggestion?.suggestedFinalPrice && !aiSuggestion?.suggestedMarkup && (
                    <Box sx={{
                      p: 2,
                      borderRadius: 1,
                      background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
                      border: '1px solid #C4B5FD',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5
                    }}>
                      <Typography sx={{ fontSize: '11px', color: '#7C3AED', fontWeight: 500 }}>
                        ℹ️ {t('noDiscountSuggestion', { defaultValue: 'No discount suggestion available for this product' })}
                      </Typography>
                    </Box>
                  )}
                  {activeTab === 1 && !aiSuggestion?.candidates?.length && (
                    <Box sx={{
                      p: 2,
                      borderRadius: 1,
                      background: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)',
                      border: '1px solid #C4B5FD',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5
                    }}>
                      <Typography sx={{ fontSize: '11px', color: '#7C3AED', fontWeight: 500 }}>
                        ℹ️ {t('noReplacementCandidates', { defaultValue: 'No replacement candidates found for this product' })}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </>
          )}

          {/* AI Suggestions Box - unified box */}
          {((activeTab === 1 && aiSuggestion?.candidates?.length > 0) || otherTypeAvailable) && (
            <Box sx={{
              p: 2,
              borderRadius: 1,
              backgroundColor: '#fff',
              border: '1px solid #374151',
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
            }}>
              {/* AI Suggestions for Replacement - inside unified box */}
              {activeTab === 1 && (
                <>
                  {/* Candidate Suggestions (when available) */}
                  {aiSuggestion?.candidates?.length > 0 && (
                    <>
                      {/* Cuadrante de la máquina: contexto para elegir */}
                      <MachineQuadrant quadrant={aiSuggestion.quadrant} />

                      {/* Text chip - shows selected candidate's suggestion */}
                      <Box sx={{ display: 'flex' }}>
                        <AICandidateChip
                          text={
                            selectedCandidateId
                              ? [...aiSuggestion.candidates, ...(aiSuggestion.volumeCandidates || [])].find(c => c.productId === selectedCandidateId)?.suggestion
                              : aiSuggestion.candidates[0]?.suggestion
                          }
                          isTextChip
                        />
                      </Box>

                      {/* Candidate selection chips: por margen (como siempre) y, si los hay, por volumen */}
                      {!aiLoading && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {[['margin', aiSuggestion.candidates], ['volume', aiSuggestion.volumeCandidates || []]].map(([group, list]) => list.length > 0 && (
                            <React.Fragment key={group}>
                              {aiSuggestion.volumeCandidates?.length > 0 && (
                                <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#6B7280', mt: group === 'volume' ? 0.5 : 0 }}>
                                  {alternativeGroupLabel(t, group)}
                                </Typography>
                              )}
                              {list.map((candidate) => (
                                <AICandidateChip
                                  key={`${group}-${candidate.productId}`}
                                  text={candidate.productName}
                                  variant="candidate"
                                  fullWidth
                                  selected={
                                    selectedCandidateId === candidate.productId ||
                                    (!selectedCandidateId && formData.newDProductId === candidate.productId)
                                  }
                                  onClick={() => {
                                    setSelectedCandidateId(candidate.productId);
                                    setFormData(prev => ({
                                      ...prev,
                                      newDProductId: candidate.productId,
                                      isAi: true
                                    }));
                                  }}
                                />
                              ))}
                            </React.Fragment>
                          ))}
                        </Box>
                      )}
                    </>
                  )}
                </>
              )}

              {/* Cross-tab availability message */}
              {otherTypeAvailable && (
                <Typography
                  sx={{
                    fontSize: '11px',
                    color: '#6366F1',
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.7, textDecoration: 'underline' }
                  }}
                  onClick={() => {
                    if (otherTypeAvailable.type === 'alza_precio_sugerido') {
                      setActiveTab(0);
                      setFormData(prev => ({ ...prev, priceDirection: 'up', experimentType: 'alza_precio_sugerido', targetPrice: '' }));
                    } else {
                      const newTab = otherTypeAvailable.type === 'promo_precio_unitario' ? 0 : 1;
                      setActiveTab(newTab);
                      setFormData(prev => ({ ...prev, experimentType: otherTypeAvailable.type }));
                    }
                    setOtherTypeAvailable(null);
                  }}
                >
                  {otherTypeAvailable.hint}. {t('clickToSwitch', { defaultValue: 'Click para cambiar →' })}
                </Typography>
              )}
            </Box>
          )}

          {/* Tab-specific Fields */}
          {activeTab === 0 && (
            <>
              {/* Direction: a price move down (discount) or up (markup). Same field, mirrored. */}
              {allowPriceIncrease && (
                <ToggleButtonGroup
                  value={formData.priceDirection}
                  exclusive
                  onChange={handlePriceDirectionChange}
                  size="small"
                  fullWidth
                  sx={{
                    mb: responsiveSpacing.sm,
                    '& .MuiToggleButton-root': {
                      textTransform: 'none',
                      fontSize: '12px',
                      fontWeight: 600,
                      py: 0.5
                    }
                  }}
                >
                  <ToggleButton value="down">
                    {`↓ ${t('priceDecrease') || 'Bajada'}`}
                  </ToggleButton>
                  <ToggleButton value="up">
                    {`↑ ${t('priceIncrease') || 'Subida'}`}
                  </ToggleButton>
                </ToggleButtonGroup>
              )}

              {/* AI Suggestion for Discount Mode */}
              {aiSuggestion?.suggestedDiscount && formData.pricingMode === 'discount' && (
                <Box sx={{ p: 2, borderRadius: 1, backgroundColor: '#fff', border: '1px solid #374151', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex' }}>
                    <AICandidateChip
                      text={aiSuggestion?.suggestion || ''}
                      selected={formData.discount === aiSuggestion?.suggestedDiscount}
                      isTextChip
                    />
                  </Box>
                  {!aiLoading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <AICandidateChip
                        text={`${aiSuggestion.suggestedDiscount}% descuento${aiSuggestion.marginAfterDiscount ? ` (${aiSuggestion.marginAfterDiscount}% margen)` : ''}`}
                        variant="candidate"
                        fullWidth
                        selected={formData.discount === aiSuggestion.suggestedDiscount}
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            discount: aiSuggestion.suggestedDiscount,
                            pricingMode: 'discount',
                            isAi: true
                          }));
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {/* AI Suggestion for Markup Mode (alza pendiente del orquestador) */}
              {aiSuggestion?.suggestedMarkup && isPriceIncrease && formData.pricingMode === 'discount' && (
                <Box sx={{ p: 2, borderRadius: 1, backgroundColor: '#fff', border: '1px solid #374151', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex' }}>
                    <AICandidateChip
                      text={aiSuggestion?.suggestion || ''}
                      selected={formData.markup === aiSuggestion?.suggestedMarkup}
                      isTextChip
                    />
                  </Box>
                  {!aiLoading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <AICandidateChip
                        text={`+${aiSuggestion.suggestedMarkup}% subida`}
                        variant="candidate"
                        fullWidth
                        selected={formData.markup === aiSuggestion.suggestedMarkup}
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            markup: aiSuggestion.suggestedMarkup,
                            pricingMode: 'discount',
                            isAi: true
                          }));
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}

              {/* AI Suggestion for Price Mode */}
              {aiSuggestion?.suggestedFinalPrice && formData.pricingMode === 'price' && (
                <Box sx={{ p: 2, borderRadius: 1, backgroundColor: '#fff', border: '1px solid #374151', display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex' }}>
                    <AICandidateChip
                      text={aiSuggestion?.priceSuggestion || ''}
                      selected={parseFloat(formData.targetPrice) === aiSuggestion?.suggestedFinalPrice}
                      isTextChip
                    />
                  </Box>
                  {!aiLoading && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <AICandidateChip
                        text={`$${aiSuggestion.suggestedFinalPrice.toFixed(2)} precio final${aiSuggestion.marginAfterDiscount ? ` (${aiSuggestion.marginAfterDiscount}% margen)` : ''}`}
                        variant="candidate"
                        fullWidth
                        selected={parseFloat(formData.targetPrice) === aiSuggestion.suggestedFinalPrice}
                        onClick={() => {
                          setFormData(prev => ({
                            ...prev,
                            targetPrice: aiSuggestion.suggestedFinalPrice.toString(),
                            pricingMode: 'price',
                            isAi: true
                          }));
                        }}
                      />
                    </Box>
                  )}
                </Box>
              )}

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={responsiveSpacing.sm} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                {formData.pricingMode === 'price' ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, minWidth: 0 }}>
                    <TextField
                      label={t('finalPrice') || 'Final price'}
                      size="small"
                      value={formData.targetPrice}
                      onChange={(e) => setFormData(prev => ({ ...prev, targetPrice: e.target.value, isAi: false }))}
                      inputProps={{ min: 0, step: 0.01, inputMode: 'decimal' }}
                      fullWidth
                    />
                    {showPriceCommaWarning && (
                      <Typography variant="caption" sx={{ color: 'error.main', mt: 0.5 }}>
                        {t('decimalCommaWarning') || 'Use . instead of commas'}
                      </Typography>
                    )}
                  </Box>
                ) : isPriceIncrease ? (
                  <TextField
                    label={t('markupLabel') || 'Subida (%)'}
                    type="number"
                    size="small"
                    value={formData.markup}
                    onChange={(e) => setFormData(prev => ({ ...prev, markup: parseInt(e.target.value, 10) || 0, isAi: false }))}
                    inputProps={{ min: 1 }}
                    fullWidth
                  />
                ) : (
                  <TextField
                    label={t('discount') || 'Discount (%)'}
                    type="number"
                    size="small"
                    value={formData.discount}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount: parseInt(e.target.value, 10) || 0, isAi: false }))}
                    inputProps={{ min: 1, max: 100 }}
                    fullWidth
                  />
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.25, minWidth: 80 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: colors.text.secondary }}>
                    {t('finalPrice') || 'Final price'}
                  </Typography>
                  <Switch
                    checked={formData.pricingMode === 'price'}
                    onChange={(event) => {
                      setFormData(prev => ({
                        ...prev,
                        pricingMode: event.target.checked ? 'price' : 'discount'
                      }));
                    }}
                    size="small"
                  />
                </Box>
              </Stack>
            </>
          )}

          {activeTab === 1 && (
            // Product Replacement Fields
            <>
              {/* Scope: full replacement (every lane) or lane expansion (a chosen subset the
                  donor cedes while staying in the machine). Mirrors the price-direction control. */}
              <ToggleButtonGroup
                value={formData.laneScope}
                exclusive
                onChange={handleLaneScopeChange}
                size="small"
                fullWidth
                sx={{
                  mb: responsiveSpacing.sm,
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontSize: '12px',
                    fontWeight: 600,
                    py: 0.5
                  }
                }}
              >
                <ToggleButton value="all">
                  {`⇄ ${t('scopeFullReplacement') || 'Reemplazo completo'}`}
                </ToggleButton>
                <ToggleButton value="partial">
                  {`➕ ${t('scopeLaneExpansion') || 'Ampliar carril'}`}
                </ToggleButton>
              </ToggleButtonGroup>

              {initialValues?.laneType === 'double' && !isLaneExpansion && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={splitMode}
                      onChange={(e) => {
                        setSplitMode(e.target.checked);
                        if (e.target.checked) {
                          setSplitSelectionId1(initialValues?.selectionId || '');
                          setSplitSelectionId2('');
                        } else {
                          setSplitNewDProductId('');
                          setSplitSelectionId1('');
                          setSplitSelectionId2('');
                        }
                      }}
                      size="small"
                    />
                  }
                  label={t('splitDoubleSlot') || 'Split into 2 simple products'}
                  sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.85rem' } }}
                />
              )}
              {splitMode ? (
                <>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Autocomplete
                      sx={{ flex: 1 }}
                      options={availableData.allProducts}
                      getOptionLabel={(option) => option.name || ''}
                      value={availableData.allProducts.find(p => p.id === formData.newDProductId) || null}
                      onChange={(_, newValue) => {
                        setFormData(prev => ({ ...prev, newDProductId: newValue?.id || '', isAi: false }));
                        setSelectedCandidateId(null);
                      }}
                      disabled={availableData.loading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('replacementProductPosition1') || 'Product - Position 1'}
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {availableData.loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    <TextField
                      size="small"
                      sx={{ width: 80 }}
                      label={t('selectionCode') || 'Cod.'}
                      value={splitSelectionId1}
                      error={!!splitError && !splitSelectionId1.trim()}
                      onChange={(e) => { setSplitSelectionId1(e.target.value); setSplitError(''); }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                    <Autocomplete
                      sx={{ flex: 1 }}
                      options={availableData.allProducts}
                      getOptionLabel={(option) => option.name || ''}
                      value={availableData.allProducts.find(p => p.id === splitNewDProductId) || null}
                      onChange={(_, newValue) => setSplitNewDProductId(newValue?.id || '')}
                      disabled={availableData.loading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('replacementProductPosition2') || 'Product - Position 2'}
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {availableData.loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                    <TextField
                      size="small"
                      sx={{ width: 80 }}
                      label={t('selectionCode') || 'Cod.'}
                      value={splitSelectionId2}
                      error={!!splitError && !splitSelectionId2.trim()}
                      onChange={(e) => { setSplitSelectionId2(e.target.value); setSplitError(''); }}
                    />
                  </Box>
                  {splitError && (
                    <Typography sx={{ fontSize: '12px', color: '#d32f2f', mt: 0.5 }}>
                      {splitError}
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Autocomplete
                    options={availableData.allProducts}
                    getOptionLabel={(option) => option.name || ''}
                    value={availableData.allProducts.find(p => p.id === formData.newDProductId) || null}
                    onChange={(_, newValue) => {
                      setFormData(prev => ({ ...prev, newDProductId: newValue?.id || '', isAi: false }));
                      setSelectedCandidateId(null);
                    }}
                    disabled={availableData.loading}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={isLaneExpansion
                          ? (t('beneficiaryProductLabel') || 'Producto que gana el carril')
                          : (t('replacementProduct') || 'Replacement Product')}
                        size="small"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {availableData.loading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                  {formData.newDProductId && showErpPrices && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: colors.text.secondary }}>
                          {t('erpEntrantPriceLabel') || 'Precio en el ERP (€)'}
                        </Typography>
                        {erpPriceLoading && <CircularProgress color="inherit" size={12} />}
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                        {ERP_TENDER_FIELDS.map(([field, labelKey]) => (
                          <TextField
                            key={field}
                            label={t(labelKey)}
                            size="small"
                            type="number"
                            required={erpPricesRequired}
                            value={erpPrices[field]}
                            onChange={(e) => setErpPrices(prev => ({ ...prev, [field]: e.target.value }))}
                            inputProps={{ min: 0.01, step: 0.01, inputMode: 'decimal' }}
                            disabled={erpPriceLoading}
                          />
                        ))}
                      </Box>
                      <Typography variant="caption" color={erpPricesRequired ? 'warning.main' : 'text.secondary'}>
                        {erpPricesRequired
                          ? (t('erpEntrantPriceRequiredHint') || 'Este producto es nuevo en el ERP y no tiene precio previo; introduce los 4 precios para poder aceptar.')
                          : (t('erpEntrantPriceHint') || 'Precio de venta del producto entrante; se programará en el ERP al aceptar.')}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </>
          )}

          <TagSelect
            label={t('tagsPlaceholder')}
            placeholder={t('tagsPlaceholder')}
            value={formData.tags}
            onChange={(nextTags) => {
              setFormData(prev => ({ ...prev, tags: Array.isArray(nextTags) ? nextTags : [] }));
            }}
            availableTags={availableTags}
          />

          {/* Innovation Checkbox - Only shown in replacement tab */}
          {activeTab === 1 && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.innovation}
                  onChange={(e) => setFormData(prev => ({ ...prev, innovation: e.target.checked }))}
                  size="small"
                />
              }
              label={t('innovation') || 'New Product in Machine'}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '14px'
                }
              }}
            />
          )}

          {/* Date Pickers - Always shown */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: responsiveSpacing.sm }}>
            <DatePicker
              label={t('startDate') || 'Start Date'}
              value={formData.dateFrom}
              onChange={handleStartDateChange}
              renderInput={(params) => <TextField {...params} size="small" sx={{ flex: 1 }} />}
            />
            <DatePicker
              label={t('endDate') || 'End Date'}
              value={formData.dateTo}
              onChange={(date) => setFormData(prev => ({ ...prev, dateTo: date }))}
              renderInput={(params) => <TextField {...params} size="small" sx={{ flex: 1 }} />}
            />
          </Box>
          {durationDisplay != null && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {`${t('experimentDurationLabel') || 'Duration'}: ${durationDisplay} ${
                durationDisplay === 1
                  ? t('weeksLabelSingular') || 'week'
                  : t('weeksLabelPlural') || 'weeks'
              }`}
            </Typography>
          )}
          {dateError && (
            <Alert severity="error" sx={{ mt: 1, py: 0.5, fontSize: '11px' }}>
              {dateError}
            </Alert>
          )}
            </Box>

            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: responsiveSpacing.sm,
              mt: responsiveSpacing.md 
            }}>
              <Button
                onClick={handleCancel}
                disabled={submitting}
                sx={{
                  color: colors.text.secondary,
                  textTransform: 'none'
                }}
              >
                {t('cancel') || 'Cancel'}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting || availableData.loading || allSlotsOccupied || machineInactive}
                variant="contained"
                sx={{
                  backgroundColor: colors.action.primary,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: colors.action.primaryDark || '#1976d2'
                  }
                }}
              >
                {submitting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  t('save') || 'Save'
                )}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default CreateExperimentForm;
